import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DiffEntry {
  field: string;
  expected: unknown;
  actual: unknown;
  severity: 'critical' | 'warning' | 'info';
  message: string;
}

interface ScenarioResult {
  scenarioId: string;
  label: string;
  passed: boolean;
  diffs: DiffEntry[];
}

interface QaReport {
  timestamp: string;
  mode: string;
  scenarioCount: number;
  passCount: number;
  failCount: number;
  warnCount: number;
  results: ScenarioResult[];
}

interface GoldenScenario {
  scenarioId: string;
  riskLevel: string;
  normalizedScore: number;
  confidence: number;
  findingsCount: number;
  recommendationsCount: number;
  riskFactors: string[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOKEN_KEY = 'ceo_qa_token';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function severityCls(severity: string): string {
  switch (severity) {
    case 'critical': return 'qa-severity-critical';
    case 'warning': return 'qa-severity-warning';
    default: return 'qa-severity-info';
  }
}

function riskBgCls(level: string): string {
  switch (level) {
    case 'GREEN': return 'qa-risk-header qa-risk-green';
    case 'YELLOW': return 'qa-risk-header qa-risk-yellow';
    case 'RED': return 'qa-risk-header qa-risk-red';
    default: return 'qa-risk-header';
  }
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '--';
  if (Array.isArray(v)) return v.length === 0 ? '[]' : v.join(', ');
  return String(v);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function QaScenarioDetail() {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const runId = searchParams.get('runId') ?? '';

  const [token] = useState(() => sessionStorage.getItem(TOKEN_KEY) ?? '');
  const [scenario, setScenario] = useState<ScenarioResult | null>(null);
  const [golden, setGolden] = useState<GoldenScenario | null>(null);
  const [report, setReport] = useState<QaReport | null>(null);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    if (!token || !runId || !scenarioId) return;

    try {
      const [runRes, goldenRes] = await Promise.all([
        fetch(`/api/admin/ceo/qa/runs/${runId}`, {
          headers: { 'x-ceo-token': token },
        }),
        fetch('/api/admin/ceo/qa/goldens', {
          headers: { 'x-ceo-token': token },
        }),
      ]);

      if (runRes.ok) {
        const runJson = await runRes.json();
        const rpt = runJson.report as QaReport;
        setReport(rpt);
        const match = rpt.results.find((r) => r.scenarioId === scenarioId);
        setScenario(match ?? null);
      } else {
        setError(`Failed to load run: ${runRes.status}`);
      }

      if (goldenRes.ok) {
        const goldenJson = await goldenRes.json();
        const match = (goldenJson.scenarios as GoldenScenario[]).find(
          (g) => g.scenarioId === scenarioId,
        );
        setGolden(match ?? null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fetch failed');
    }
  }, [token, runId, scenarioId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!token) {
    return (
      <div className="qa-page">
        <p className="qa-error">Not authenticated. Please log in from the QA Dashboard.</p>
        <button className="btn btn-primary" onClick={() => navigate('/admin/qa')}>
          Go to QA Dashboard
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="qa-page">
        <p className="qa-error">{error}</p>
        <button className="qa-back-link" onClick={() => navigate('/admin/qa')}>
          &larr; Back to QA Dashboard
        </button>
      </div>
    );
  }

  if (!scenario) {
    return (
      <div className="qa-page">
        <p className="qa-loading">Loading scenario detail...</p>
      </div>
    );
  }

  const riskLevel = golden?.riskLevel ?? '--';
  const score = golden?.normalizedScore ?? '--';
  const confidence = golden?.confidence ?? '--';
  const criticalDiffs = scenario.diffs.filter((d) => d.severity === 'critical');
  const warningDiffs = scenario.diffs.filter((d) => d.severity === 'warning');
  const infoDiffs = scenario.diffs.filter((d) => d.severity === 'info');

  return (
    <div className="qa-page">
      {/* Banner */}
      <div className="qa-banner-info">
        Informational only &mdash; Internal QA view. Accuracy not assured.
      </div>

      {/* Back link */}
      <button className="qa-back-link" onClick={() => navigate('/admin/qa')}>
        &larr; Back to QA Dashboard
      </button>

      {/* Risk header card */}
      <div className={riskBgCls(riskLevel)}>
        <div className="qa-risk-label">{riskLevel}</div>
        <h2>{scenario.scenarioId}</h2>
        <p className="qa-scenario-label">{scenario.label}</p>
        <div className="qa-risk-status">
          {scenario.passed ? (
            <span className="qa-chip qa-chip-pass">PASS</span>
          ) : (
            <span className="qa-chip qa-chip-fail">FAIL</span>
          )}
        </div>
      </div>

      {/* Score & Confidence */}
      <div className="qa-metrics-row">
        <div className="qa-metric-card">
          <div className="qa-metric-label">Normalized Score</div>
          <div className="qa-metric-value">{score}</div>
          <div className="qa-metric-sub">0 &ndash; 10 scale</div>
        </div>
        <div className="qa-metric-card">
          <div className="qa-metric-label">Confidence</div>
          <div className="qa-metric-value">
            {typeof confidence === 'number' ? `${(confidence * 100).toFixed(1)}%` : confidence}
          </div>
          <div className="qa-metric-sub">Source agreement</div>
        </div>
        <div className="qa-metric-card">
          <div className="qa-metric-label">Findings</div>
          <div className="qa-metric-value">{golden?.findingsCount ?? '--'}</div>
          <div className="qa-metric-sub">Total issues</div>
        </div>
        <div className="qa-metric-card">
          <div className="qa-metric-label">Recommendations</div>
          <div className="qa-metric-value">{golden?.recommendationsCount ?? '--'}</div>
          <div className="qa-metric-sub">Action items</div>
        </div>
      </div>

      {/* Risk Factors */}
      <section className="qa-section">
        <h3>Risk Factors</h3>
        {golden?.riskFactors && golden.riskFactors.length > 0 ? (
          <div className="qa-risk-factors">
            {golden.riskFactors.map((f, i) => (
              <span key={i} className="qa-risk-pill">{f}</span>
            ))}
          </div>
        ) : (
          <p className="qa-empty">No risk factors detected.</p>
        )}
      </section>

      {/* Diffs / Evidence */}
      {scenario.diffs.length > 0 && (
        <section className="qa-section">
          <h3>
            Diffs ({criticalDiffs.length}C / {warningDiffs.length}W / {infoDiffs.length}I)
          </h3>
          <div className="qa-evidence-list">
            {scenario.diffs.map((d, i) => (
              <div key={i} className={`qa-evidence-card ${severityCls(d.severity)}`}>
                <div className="qa-evidence-header">
                  <span className={`qa-severity-tag ${severityCls(d.severity)}`}>
                    {d.severity.toUpperCase()}
                  </span>
                  <span className="qa-evidence-field">{d.field}</span>
                </div>
                <p className="qa-evidence-message">{d.message}</p>
                <div className="qa-evidence-values">
                  <div>
                    <span className="qa-evidence-label">Expected:</span>{' '}
                    <code>{formatValue(d.expected)}</code>
                  </div>
                  <div>
                    <span className="qa-evidence-label">Actual:</span>{' '}
                    <code>{formatValue(d.actual)}</code>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Golden Comparison */}
      {golden && (
        <section className="qa-section">
          <h3>Golden Comparison</h3>
          <div className="qa-comparison">
            <table className="qa-runs-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Golden</th>
                  <th>Current</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Risk Level</td>
                  <td>{golden.riskLevel}</td>
                  <td>{riskLevel}</td>
                </tr>
                <tr>
                  <td>Score</td>
                  <td>{golden.normalizedScore}</td>
                  <td>{score}</td>
                </tr>
                <tr>
                  <td>Confidence</td>
                  <td>{golden.confidence}</td>
                  <td>{confidence}</td>
                </tr>
                <tr>
                  <td>Findings</td>
                  <td>{golden.findingsCount}</td>
                  <td>{golden.findingsCount}</td>
                </tr>
                <tr>
                  <td>Recommendations</td>
                  <td>{golden.recommendationsCount}</td>
                  <td>{golden.recommendationsCount}</td>
                </tr>
                <tr>
                  <td>Risk Factors</td>
                  <td>{golden.riskFactors.length > 0 ? golden.riskFactors.join(', ') : '--'}</td>
                  <td>{golden.riskFactors.length > 0 ? golden.riskFactors.join(', ') : '--'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Report metadata */}
      {report && (
        <p className="qa-footer">
          Run: {runId} &middot; Mode: {report.mode} &middot; {report.scenarioCount} scenarios
        </p>
      )}
    </div>
  );
}
